import type { EditorHost } from '@blocksuite/block-std';
import { WithDisposable } from '@blocksuite/block-std';
import type { AffineAIPanelState } from '@blocksuite/blocks';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';

import { PiqueQuizEmbedBlockSpec } from '../../../../components/blocksuite/block-suite-editor/specs/custom/pique/quiz/quiz-embed-block';
import { getAIPanel } from '../utils/edgeless';
import { AIProvider } from '../provider';
import { createQuizTemplate, type QuizQuestionData, type QuizTemplate } from '../quiz/template';

function seededRNG(seed: string) {
  const a = 1664525;
  const c = 1013904223;
  const m = 2 ** 32;
  const seededNumber = stringToNumber(seed);
  return ((a * seededNumber + c) % m) / m;

  function stringToNumber(str: string) {
    let res = 0;
    for (let i = 0; i < str.length; i++) {
      const character = str.charCodeAt(i);
      res += character;
    }
    return res;
  }
}

const getImageUrlByKeyword = async (
  keyword: string,
  width: number,
  height: number
): Promise<string> => {
  try {
    if (!AIProvider.photoEngine) {
      console.warn('Photo engine not initialized');
      return '';
    }

    const photos = await AIProvider.photoEngine.searchImages({
      query: keyword,
      width,
      height,
    });

    if (!photos?.length) {
      return '';
    }

    const rng = seededRNG(`${width}.${height}.${keyword}`);
    return photos[Math.floor(rng * photos.length)] ?? '';
  } catch (error) {
    console.warn('Failed to fetch image:', error);
    return '';
  }
};

export class QuizBuilder {
  constructor(private readonly host: EditorHost) { }

  async buildQuiz(text: string): Promise<{
    template: QuizTemplate;
    images: { id: string; url: string }[];
  }> {
    try {
      const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/);
      const jsonStr = jsonMatch?.[1] ?? text;
      const quizData = JSON.parse(jsonStr) as { title?: string; questions: QuizQuestionData[] };

      const template = createQuizTemplate({
        response: JSON.stringify(quizData),
        title: quizData.title || 'Quiz',
      });

      if (!template || !template.blocks) {
        throw new Error('Invalid quiz template');
      }

      // Fetch images for each question that has keywords
      const images = await Promise.all(
        template.blocks
          .filter(block => block.imageKeywords)
          .map(async block => {
            if (!block.imageKeywords) return { id: block.id, url: '' };
            const url = await getImageUrlByKeyword(
              block.imageKeywords,
              800,
              400
            );
            return {
              id: block.id,
              url,
            };
          })
      );

      return {
        template: {
          ...template,
          questions: quizData.questions.map((q: QuizQuestionData, index: number) => {
            const block = template.blocks[index];
            return {
              id: block?.id ?? `question-${index}`,
              text: block?.text ?? q.question,
              type: block?.type ?? 'question',
              xywh: block?.xywh,
              imageKeywords: block?.imageKeywords,
              meta: block?.meta,
              question: q.question,
              options: q.options,
              answer: q.answer,
              explanation: q.explanation,
            };
          }),
        },
        images: images.filter(img => img.url),
      };
    } catch (error) {
      console.error('Failed to parse quiz data:', error);
      throw error;
    }
  }

  async insertQuizBlock(
    template: QuizTemplate,
    images: { id: string; url: string }[]
  ) {
    const page = (this.host as any).page;
    if (!page) return;

    // Create quiz block with template data
    const quizData = {
      ...template,
      questions:
        template.questions?.map(q => ({
          ...q,
          imageUrl: images.find(img => img.id === q.id)?.url,
        })) || [],
    };

    // Insert the quiz block
    const quizBlock = page.addBlock('pique-quiz', {
      quiz: JSON.stringify(quizData),
    });

    // Focus the editor on the new block
    const blockSelection = this.host.std.selection.create('block', {
      blockId: quizBlock.id,
    });
    this.host.std.selection.setGroup('note', [blockSelection]);
  }
}

@customElement('ai-quiz-renderer')
export class AIQuizRenderer extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }
  `;

  private readonly _editorContainer: Ref<HTMLDivElement> =
    createRef<HTMLDivElement>();
  private _templateReadyResolve!: (value: void) => void;
  private readonly _templateReady = new Promise<void>(resolve => {
    this._templateReadyResolve = resolve;
  });

  @property({ attribute: false })
  accessor text!: string;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor ctx:
    | {
      get(): Record<string, unknown>;
      set(data: Record<string, unknown>): void;
    }
    | undefined = undefined;

  get templateReady(): Promise<void> {
    return this._templateReady;
  }

  override async firstUpdated() {
    requestAnimationFrame(() => {
      const builder = new QuizBuilder(this.host);
      builder
        .buildQuiz(this.text)
        .then(async ({ template, images }) => {
          if (this.ctx) {
            this.ctx.set({
              data: template,
              images: images,
            });

            // Insert quiz block
            await builder.insertQuizBlock(template, images);

            getAIPanel(this.host)
              ?.shadowRoot?.querySelector('ai-panel-answer')
              ?.requestUpdate();
            this._templateReadyResolve();
          }
        })
        .catch(error => {
          console.error('Failed to build quiz:', error);
          this.ctx?.set({ error: 'Failed to build quiz' });
          this._templateReadyResolve();
        });
    });
  }

  override render() {
    return html` <div ${ref(this._editorContainer)}></div> `;
  }
}

export function createQuizDataProcessor(
  host: EditorHost,
  ctx: {
    get(): Record<string, unknown>;
    set(data: Record<string, unknown>): void;
  }
) {
  // Register the quiz block if not already registered
  if (!(host as any).page?.schema.flavourSchemaMap.has('pique-quiz')) {
    (host as any).page?.schema.register(PiqueQuizEmbedBlockSpec);
  }

  return (answer: string, state?: AffineAIPanelState) => {
    if (state === 'generating') {
      getAIPanel(host)?.generatingElement?.updateLoadingProgress?.(2);
      return nothing;
    }

    if (state !== 'finished' && state !== 'error') {
      return nothing;
    }

    return html`
      <ai-quiz-renderer
        .text=${answer}
        .host=${host}
        .ctx=${ctx}
      ></ai-quiz-renderer>
    `;
  };
}